import { useReducer, useMemo, useCallback } from 'react'
import {
    getDefaultDataConfig,
    getDefaultQueryConfig,
    getDefaultViewConfig,
} from '../../utils/summary'
import { normalizeSavedConfig } from '../../utils/summary'
import { splitConfig, mergeConfigs } from '../../utils/summary'

/**
 * Action types for the summary config reducer.
 * Separated into three categories:
 * - Data actions: Trigger API refetch (affect what data is queried)
 * - Query actions: Trigger API refetch (affect how data is aggregated)
 * - View actions: NO API refetch (only affect rendering)
 */
export const ACTIONS = {
    // Data actions (trigger API refetch)
    ADD_METRIC: 'ADD_METRIC',
    REMOVE_METRIC: 'REMOVE_METRIC',
    UPDATE_METRIC: 'UPDATE_METRIC',
    SET_DRILL_ATTRIBUTE: 'SET_DRILL_ATTRIBUTE',
    SET_CATEGORY_AXIS: 'SET_CATEGORY_AXIS',
    SET_FILTER: 'SET_FILTER',
    SET_SAVED_FILTER: 'SET_SAVED_FILTER',
    CLEAR_FILTERS: 'CLEAR_FILTERS',

    // Query actions (trigger API refetch)
    SET_PERIOD: 'SET_PERIOD',
    SET_TIME_RANGE: 'SET_TIME_RANGE',
    SET_CUSTOM_RANGE: 'SET_CUSTOM_RANGE',
    SET_RELATIVE_RANGE: 'SET_RELATIVE_RANGE',
    SET_CALENDAR_TYPE: 'SET_CALENDAR_TYPE',
    SET_SORT_COLUMNS: 'SET_SORT_COLUMNS',
    SET_DISPLAY_ROWS: 'SET_DISPLAY_ROWS',
    TOGGLE_TIME_SERIES: 'TOGGLE_TIME_SERIES',

    // View actions (NO API refetch)
    SET_VIEW: 'SET_VIEW',
    SET_SERIES_TYPE: 'SET_SERIES_TYPE',
    SET_SERIES_TYPE_FOR_METRIC: 'SET_SERIES_TYPE_FOR_METRIC',
    SET_STACKED: 'SET_STACKED',
    SET_CHART_TITLE: 'SET_CHART_TITLE',
    UPDATE_STYLE: 'UPDATE_STYLE',
    SET_THEME: 'SET_THEME',
    SET_KPI_COMPARE_TYPE: 'SET_KPI_COMPARE_TYPE',
    SET_KPI_COMPARE_TARGET: 'SET_KPI_COMPARE_TARGET',

    // Bulk actions
    LOAD_CONFIG: 'LOAD_CONFIG',
    RESET: 'RESET',
}

/**
 * Initial state using default config factories.
 */
const getInitialState = () => ({
    dataConfig: getDefaultDataConfig(),
    queryConfig: getDefaultQueryConfig(),
    viewConfig: getDefaultViewConfig(),
})

/**
 * Reducer for managing summary configuration state.
 * Handles all config changes with clear separation between
 * data/query changes (trigger refetch) and view changes (no refetch).
 */
export const summaryConfigReducer = (state, action) => {
    switch (action.type) {
        // ═══════════════════════════════════════════════════════════════
        // DATA ACTIONS (trigger API refetch)
        // ═══════════════════════════════════════════════════════════════

        case ACTIONS.ADD_METRIC:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    selectedItems: [...state.dataConfig.selectedItems, action.payload],
                },
            }

        case ACTIONS.REMOVE_METRIC:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    selectedItems: state.dataConfig.selectedItems.filter((item, index) =>
                        action.payload.index !== undefined
                            ? index !== action.payload.index
                            : item.measureName !== action.payload.measureName
                    ),
                },
            }

        case ACTIONS.UPDATE_METRIC:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    selectedItems: state.dataConfig.selectedItems.map((item, index) =>
                        index === action.payload.index
                            ? { ...item, ...action.payload.updates }
                            : item
                    ),
                },
            }

        case ACTIONS.SET_DRILL_ATTRIBUTE:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    drillAttribute: action.payload,
                    usedDrillAttributes: action.payload
                        ? [...new Set([...state.dataConfig.usedDrillAttributes, action.payload])]
                        : state.dataConfig.usedDrillAttributes,
                },
            }

        case ACTIONS.SET_CATEGORY_AXIS:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    categoryAxis: action.payload,
                },
            }

        case ACTIONS.SET_FILTER:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    filter: action.payload,
                },
            }

        case ACTIONS.SET_SAVED_FILTER:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    savedFilter: action.payload,
                },
            }

        case ACTIONS.CLEAR_FILTERS:
            return {
                ...state,
                dataConfig: {
                    ...state.dataConfig,
                    filter: null,
                    savedFilter: null,
                },
            }

        // ═══════════════════════════════════════════════════════════════
        // QUERY ACTIONS (trigger API refetch)
        // ═══════════════════════════════════════════════════════════════

        case ACTIONS.SET_PERIOD:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    period: action.payload,
                },
            }

        case ACTIONS.SET_TIME_RANGE:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    timeRange: action.payload,
                    from: null,
                    to: null,
                },
            }

        case ACTIONS.SET_CUSTOM_RANGE:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    timeRange: 'Custom',
                    from: action.payload.from,
                    to: action.payload.to,
                    fromPeriod: null,
                    toPeriod: null,
                },
            }

        case ACTIONS.SET_RELATIVE_RANGE:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    timeRange: 'Custom',
                    fromPeriod: action.payload.fromPeriod,
                    toPeriod: action.payload.toPeriod,
                    from: null,
                    to: null,
                },
            }

        case ACTIONS.SET_CALENDAR_TYPE:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    calendarType: action.payload,
                },
            }

        case ACTIONS.SET_SORT_COLUMNS:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    sortColumns: action.payload,
                },
            }

        case ACTIONS.SET_DISPLAY_ROWS:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    displayRows: action.payload,
                },
            }

        case ACTIONS.TOGGLE_TIME_SERIES:
            return {
                ...state,
                queryConfig: {
                    ...state.queryConfig,
                    tableTimeSeries: action.payload,
                },
            }

        // ═══════════════════════════════════════════════════════════════
        // VIEW ACTIONS (NO API refetch)
        // ═══════════════════════════════════════════════════════════════

        case ACTIONS.SET_VIEW:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    view: action.payload,
                },
            }

        case ACTIONS.SET_SERIES_TYPE:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    seriesType: action.payload,
                },
            }

        case ACTIONS.SET_SERIES_TYPE_FOR_METRIC: {
            const newSeriesTypeMap = new Map(state.viewConfig.seriesTypeMap)
            newSeriesTypeMap.set(action.payload.metricName, action.payload.seriesType)
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    seriesTypeMap: newSeriesTypeMap,
                },
            }
        }

        case ACTIONS.SET_STACKED:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    stacked: action.payload,
                },
            }

        case ACTIONS.SET_CHART_TITLE:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    chartTitle: action.payload,
                },
            }

        case ACTIONS.UPDATE_STYLE:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    styleOverrides: {
                        ...state.viewConfig.styleOverrides,
                        ...action.payload,
                    },
                },
            }

        case ACTIONS.SET_THEME:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    themeID: action.payload,
                },
            }

        case ACTIONS.SET_KPI_COMPARE_TYPE:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    kpiCompareType: action.payload,
                },
            }

        case ACTIONS.SET_KPI_COMPARE_TARGET:
            return {
                ...state,
                viewConfig: {
                    ...state.viewConfig,
                    kpiCompareTarget: action.payload,
                },
            }

        // ═══════════════════════════════════════════════════════════════
        // BULK ACTIONS
        // ═══════════════════════════════════════════════════════════════

        case ACTIONS.LOAD_CONFIG: {
            const normalized = normalizeSavedConfig(
                action.payload.config,
                action.payload.workspaceMetadata
            )
            const split = splitConfig(normalized)
            return {
                dataConfig: split.dataConfig,
                queryConfig: split.queryConfig,
                viewConfig: split.viewConfig,
            }
        }

        case ACTIONS.RESET:
            return getInitialState()

        default:
            return state
    }
}

/**
 * Hook that provides config state and memoized action creators.
 *
 * @param {Object|null} initialConfig - Optional initial config to load
 * @param {Object|null} workspaceMetadata - Optional workspace metadata for normalization
 * @returns {Object} - State, actions, dispatch, and helper functions
 */
export const useSummaryConfig = (initialConfig = null, workspaceMetadata = null) => {
    const [state, dispatch] = useReducer(
        summaryConfigReducer,
        { initialConfig, workspaceMetadata },
        (init) => {
            if (!init.initialConfig) return getInitialState()
            const normalized = normalizeSavedConfig(init.initialConfig, init.workspaceMetadata)
            return splitConfig(normalized)
        }
    )

    // Memoized action creators
    const actions = useMemo(
        () => ({
            // Data actions
            addMetric: (metric) => dispatch({ type: ACTIONS.ADD_METRIC, payload: metric }),
            removeMetric: (payload) => dispatch({ type: ACTIONS.REMOVE_METRIC, payload }),
            updateMetric: (index, updates) =>
                dispatch({ type: ACTIONS.UPDATE_METRIC, payload: { index, updates } }),
            setDrillAttribute: (attr) =>
                dispatch({ type: ACTIONS.SET_DRILL_ATTRIBUTE, payload: attr }),
            setCategoryAxis: (axis) => dispatch({ type: ACTIONS.SET_CATEGORY_AXIS, payload: axis }),
            setFilter: (filter) => dispatch({ type: ACTIONS.SET_FILTER, payload: filter }),
            setSavedFilter: (filter) =>
                dispatch({ type: ACTIONS.SET_SAVED_FILTER, payload: filter }),
            clearFilters: () => dispatch({ type: ACTIONS.CLEAR_FILTERS }),

            // Query actions
            setPeriod: (period) => dispatch({ type: ACTIONS.SET_PERIOD, payload: period }),
            setTimeRange: (range) => dispatch({ type: ACTIONS.SET_TIME_RANGE, payload: range }),
            setCustomRange: (from, to) =>
                dispatch({ type: ACTIONS.SET_CUSTOM_RANGE, payload: { from, to } }),
            setRelativeRange: (fromPeriod, toPeriod) =>
                dispatch({ type: ACTIONS.SET_RELATIVE_RANGE, payload: { fromPeriod, toPeriod } }),
            setCalendarType: (type) => dispatch({ type: ACTIONS.SET_CALENDAR_TYPE, payload: type }),
            setSortColumns: (columns) =>
                dispatch({ type: ACTIONS.SET_SORT_COLUMNS, payload: columns }),
            setDisplayRows: (rows) => dispatch({ type: ACTIONS.SET_DISPLAY_ROWS, payload: rows }),
            toggleTimeSeries: (enabled) =>
                dispatch({ type: ACTIONS.TOGGLE_TIME_SERIES, payload: enabled }),

            // View actions
            setView: (view) => dispatch({ type: ACTIONS.SET_VIEW, payload: view }),
            setSeriesType: (type) => dispatch({ type: ACTIONS.SET_SERIES_TYPE, payload: type }),
            setSeriesTypeForMetric: (metricName, seriesType) =>
                dispatch({
                    type: ACTIONS.SET_SERIES_TYPE_FOR_METRIC,
                    payload: { metricName, seriesType },
                }),
            setStacked: (stacked) => dispatch({ type: ACTIONS.SET_STACKED, payload: stacked }),
            setChartTitle: (title) => dispatch({ type: ACTIONS.SET_CHART_TITLE, payload: title }),
            updateStyle: (styles) => dispatch({ type: ACTIONS.UPDATE_STYLE, payload: styles }),
            setTheme: (themeID) => dispatch({ type: ACTIONS.SET_THEME, payload: themeID }),
            setKpiCompareType: (type) =>
                dispatch({ type: ACTIONS.SET_KPI_COMPARE_TYPE, payload: type }),
            setKpiCompareTarget: (target) =>
                dispatch({ type: ACTIONS.SET_KPI_COMPARE_TARGET, payload: target }),

            // Bulk actions
            loadConfig: (config, workspaceMetadata) =>
                dispatch({ type: ACTIONS.LOAD_CONFIG, payload: { config, workspaceMetadata } }),
            reset: () => dispatch({ type: ACTIONS.RESET }),
        }),
        []
    )

    // Helper to get merged config (for saving)
    const getMergedConfig = useCallback(() => {
        return mergeConfigs(state.dataConfig, state.queryConfig, state.viewConfig)
    }, [state])

    return {
        state,
        actions,
        dispatch,
        getMergedConfig,
    }
}
